<script setup>
	import { ref, computed } from 'vue';
	import { Swiper, SwiperSlide } from 'swiper/vue/swiper-vue';
	import { Navigation, Pagination, Keyboard, A11y } from 'swiper';
	import 'swiper/scss';
	import { DsIconButton, DsButton, DsText, DsButtonPlay, dsUseIsMobile } from '@coloplast/design-system';
	import VideoPopup from 'shared/components/VideoElements/videoPopup.vue';
	import { track_content_engagement } from 'shared/services/tracking/contentTrackingService.ts';

	const props = defineProps({
		vm: {
			type: Object,
			required: true
		},
	});

	const { isMobile } = dsUseIsMobile();

	//refs
	const
		slides = ref(props?.vm?.BaseViewModel?.CarouselItems),
		slidesCount = ref(props?.vm?.BaseViewModel?.CarouselItems?.length),
		slidesPerView = ref(0);

	const showNavigation = computed(() => Math.ceil(slidesCount.value / slidesPerView.value) > 1);

	function initCarousel(swiper) {
		slidesPerView.value = swiper?.params?.slidesPerView;
	}

	function onResize(swiper) {
		slidesPerView.value = swiper?.params?.slidesPerView;
		swiper.slideTo(0);
	}

	// Video Carousel logic
	const isVideoCarousel = computed(() => props.vm?.BaseViewModel?.CarouselItems?.some(item => item.VideoUrl));

	const videoIsPlaying = ref(false);
	const videoUrl = ref(null);

	const setVideoUrl = (url, headline) => {
		videoUrl.value = url;
		videoIsPlaying.value = true;
		track_content_engagement('video', 'start', headline || 'no_data', url);
	}

	//Swiper settings
	const formattedSlidesPerView = computed(() => {
		if (!isVideoCarousel.value) {
			return props.vm.BaseViewModel.CarouselItems?.length > 1 ? 1.1 : 1;
		} else {
			return 2.4;
		}
	});

	const settings = {
		speed: 500,
		mousewheel: true,
		keyboard: true,
		watchOverflow: true,
		breakpoints: {
			768: {
				slidesPerView: formattedSlidesPerView.value,
				spaceBetween: 24,
			},
			0: {
				slidesPerView: props.vm.BaseViewModel.CarouselItems?.length > 1 ? 1.01 : 1,
				spaceBetween: 12,
			},
		},
		pagination: {
			el: `.pagination-${props.vm.BaseViewModel.CurrentBlockId}`,
			type: 'progressbar',
			progressbarFillClass: 'c-content-library-carousel__progressbar--fill',
		},
		navigation: {
			nextEl: `.next-${props.vm.BaseViewModel.CurrentBlockId}`,
			prevEl: `.prev-${props.vm.BaseViewModel.CurrentBlockId}`,
		},
		modules: [Navigation, Pagination, Keyboard, A11y],
	};
</script>

<template>
	<div class="c-content-library-carousel__container">
		<div class="c-content-library-carousel__toparea --applicationArea">
			<div class="c-content-library-carousel__intro">
				<DsText
					v-if="vm.CurrentBlock.Headline"
					html-element="h2"
					text-type="heading-3xl"
					text-style="medium"
					class="c-content-library-carousel__headline"
				>
					{{ vm.CurrentBlock.Headline }}
				</DsText>
				<DsText
					v-if="vm.CurrentBlock.Description"
					html-element="div"
					text-type="body-md"
					text-style="regular"
					class="c-content-library-carousel__description"
				>
					<span v-html="vm.CurrentBlock.Description"></span>
				</DsText>
			</div>
			<div
				class="c-content-library-carousel__nav --muted"
				:class="{ '--hidden': !showNavigation }"
			>
				<DsIconButton
					aria-label="Previous"
					icon-name="chevron-left"
					type="button"
					size="md"
					:class="`prev-${vm.BaseViewModel.CurrentBlockId}`"
				>
				</DsIconButton>
				<DsIconButton
					aria-label="Next"
					icon-name="chevron-right"
					type="button"
					size="md"
					:class="`next-${vm.BaseViewModel.CurrentBlockId}`"
				>
				</DsIconButton>
			</div>
		</div>
		<div
			v-show="showNavigation"
			class="c-content-library-carousel__progressbar"
			:class="`pagination-${vm.BaseViewModel.CurrentBlockId}`"
		>
		</div>
		<Swiper
			v-if="slidesCount"
			v-bind="settings"
			class="c-content-library-carousel__productappswiper"
			@init="initCarousel"
			@resize="swiper => onResize(swiper)"
		>
			<SwiperSlide
				v-for="(slide, index) in slides"
				:key="index"
			>
				<template v-if="!isVideoCarousel">
					<div class="c-content-library-carousel__slidescontainer">
						<picture class="c-content-library-carousel__slideimagebox --left">
							<source
								media="(max-width: 767px)"
								:srcset="`${slide.LeftSideImageUrl}?im=Resize=(327,134)`"
							/>
							<img
								:src="`${slide.LeftSideImageUrl}?im=Resize=(732,400)`"
								:alt="slide.LeftSideImageAltText || slide.Caption"
							/>
						</picture>
						<picture class="c-content-library-carousel__slideimagebox --right">
							<source
								media="(max-width: 767px)"
								:srcset="`${slide.RightSideImageUrl}?im=Resize=(327,134)`"
							/>
							<img
								:src="`${slide.RightSideImageUrl}?im=Resize=(516,400)`"
								:alt="slide.RightSideImageAltText || slide.Caption"
							/>
						</picture>
					</div>
					<DsText
						v-if="slide.Caption"
						html-element="div"
						text-type="body-sm"
						text-style="regular"
					>
						<span v-html="slide.Caption" class="c-content-library-carousel__slidecaption"></span>
					</DsText>
				</template>
				<template v-if="isVideoCarousel">
					<article
						class="c-content-library-carousel__video-card"
						@click="setVideoUrl(slide.VideoUrl, slide.Caption)"
					>
						<figure class="c-content-library-carousel__video-figure">
							<div class="c-content-library-carousel__image-wrapper">
								<img
									:alt="slide.Caption"
									:src="slide.PreviewImageUrl"
									class="c-content-library-carousel__video-image"
								/>
								<div class="c-content-library-carousel__video-button">
									<DsButtonPlay
										aria-label-play="Play video"
										:size="isMobile ? 'md' : 'lg'"
										variant="inverted"
									/>
								</div>
							</div>
							<figcaption class="c-content-library-carousel__video-figcaption">
								<DsText
									v-if="slide.Caption"
									html-element="h4"
									text-type="heading-xl"
									text-style="medium"
								>
									{{ slide.Caption }}
								</DsText>
							</figcaption>
						</figure>
					</article>
				</template>
			</SwiperSlide>
		</Swiper>
		<div
			v-if="vm.BaseViewModel.LinkUrl"
			class="c-content-library-carousel__viewall"
		>
			<DsButton
				variant="link"
				icon-end="navigate-forward"
				:href="vm.BaseViewModel.LinkUrl"
				size="md"
				:disabled="null"
			>
				{{ vm.CurrentBlock.LinkText }}
			</DsButton>
		</div>
		<VideoPopup
			v-if="videoIsPlaying"
			:video-url="videoUrl"
			@close-popup="videoIsPlaying = false"
		/>
	</div>
</template>
